/** @module Starts the camera while a screen wants it and reports why it could not. */
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import { Camera, type CameraOptions, cameras } from "@/features/scan/camera";

export function useCamera(
  video: RefObject<HTMLVideoElement | null>,
  active: boolean,
  options: CameraOptions = {},
) {
  const [error, setError] = useState<string | null>(null);
  const [available, setAvailable] = useState<MediaDeviceInfo[]>([]);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torch, setTorchState] = useState(false);
  const camera = useRef<Camera | null>(null);
  const { idealWidth, deviceId } = options;

  useEffect(() => {
    if (!active) return;
    const current = new Camera();
    camera.current = current;
    const element = video.current;
    let cancelled = false;
    // Read through a function: the flag is set in the cleanup, after this closure.
    const leftScreen = () => cancelled;

    void (async () => {
      if (!element) return;
      try {
        await current.start(element, { idealWidth, deviceId });
        // The screen may have been left while the permission dialog was open.
        if (leftScreen()) {
          current.stop(element);
          return;
        }
        setError(null);
        setTorchAvailable(current.hasTorch);
        // Labels stay empty until permission is given, so the list is read afterwards.
        setAvailable(await cameras());
      } catch (cause) {
        // Without HTTPS the browser refuses the camera; that is the usual cause here.
        setError(cause instanceof Error ? cause.name : "unknown");
      }
    })();

    return () => {
      cancelled = true;
      current.stop(element);
      camera.current = null;
      setTorchAvailable(false);
      setTorchState(false);
    };
  }, [active, video, idealWidth, deviceId]);

  const toggleTorch = useCallback(async () => {
    const next = !torch;
    try {
      await camera.current?.setTorch(next);
      setTorchState(next);
    } catch {
      // A lamp that refuses is not worth an error on screen; the button simply stays off.
    }
  }, [torch]);

  return { error, torchAvailable, torch, toggleTorch, available };
}
